import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { FaUpload, FaTrash, FaDownload } from 'react-icons/fa';
import api from '../../services/api';
import { getFileUrl } from '../../config';
import { useI18n } from '../../i18n/I18nContext';

function VehiculeForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { t } = useI18n();

  const [formData, setFormData] = useState({
    plateNumber: '',
    brand: '',
    model: '',
    type: '',
    capacity: '',
    status: 'AVAILABLE',
    year: new Date().getFullYear(),
    color: '',
    registrationNumber: '',
    insuranceNumber: '',
    lastMaintenanceDate: '',
    nextMaintenanceDate: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [isUploadingDocs, setIsUploadingDocs] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      fetchVehicule();
      fetchVehiculeDocuments();
    }
  }, [id]);

  const fetchVehicule = async () => {
    try {
      const response = await api.get(`/transporteur/vehicules/${id}`);
      setFormData(response.data);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchVehiculeDocuments = async () => {
    try {
      const response = await api.get(`/transporteur/vehicules/${id}/documents`);
      setExistingDocuments(response.data || []);
    } catch (_) { }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const url = isEditMode
        ? `/transporteur/vehicules/${id}`
        : '/transporteur/vehicules';

      const method = isEditMode ? 'put' : 'post';

      const response = await api[method](url, formData);
      const vehiculeId = isEditMode ? id : (response.data?.id || response.data);

      // If queued documents, upload them
      if (documents.length) {
        setIsUploadingDocs(true);
        for (const doc of documents) {
          const fd = new FormData();
          fd.append('file', doc.file);
          if (doc.name) fd.append('name', doc.name);
          if (doc.type) fd.append('type', doc.type);
          await api.post(`/transporteur/vehicules/${vehiculeId}/documents`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        setDocuments([]);
        if (isEditMode) {
          await fetchVehiculeDocuments();
        }
        setIsUploadingDocs(false);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/vehicules');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onFilesSelected = (files) => {
    const MAX_FILE_MB = 25;
    const rejected = [];
    const accepted = [];
    Array.from(files).forEach(f => {
      const sizeMb = f.size / (1024 * 1024);
      if (sizeMb > MAX_FILE_MB) {
        rejected.push({ name: f.name, sizeMb: sizeMb.toFixed(1) });
      } else {
        accepted.push({ file: f, name: f.name, type: 'carte_grise' });
      }
    });
    if (rejected.length) {
      setError(t('common.error') + `: ${rejected.map(r => r.name + ' (' + r.sizeMb + 'MB)').join(', ')}`);
    }
    if (accepted.length) {
      setDocuments(prev => [...prev, ...accepted]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length) {
      onFilesSelected(e.target.files);
      e.target.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length) {
      onFilesSelected(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const updateDocumentMeta = (index, key, value) => {
    setDocuments(prev => prev.map((doc, i) => i === index ? { ...doc, [key]: value } : doc));
  };

  const removeDocument = (index) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <DashboardLayout>
      <div className="container mt-4">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card">
              <div className="card-body">
                <h2 className="card-title mb-4">
                  {isEditMode ? t('vehicles.editTitle') : t('vehicles.addTitle')}
                </h2>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-circle me-2"></i>
                    {error}
                  </div>
                )}


                {success && (
                  <div className="alert alert-success" role="alert">
                    <i className="bi bi-check-circle me-2"></i>
                    {isEditMode ? t('vehicles.updatedSuccess') : t('vehicles.addedSuccess')}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    {/* Basic Information */}
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          name="plateNumber"
                          id="plateNumber"
                          value={formData.plateNumber}
                          onChange={handleChange}
                          required
                          placeholder={t('vehicles.form.plateNumber')}
                        />
                        <label htmlFor="plateNumber">{t('vehicles.form.plateNumber')} *</label>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          name="registrationNumber"
                          id="registrationNumber"
                          value={formData.registrationNumber}
                          onChange={handleChange}
                          required
                          placeholder={t('vehicles.form.registrationNumber')}
                        />
                        <label htmlFor="registrationNumber">{t('vehicles.form.registrationNumber')} *</label>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          name="brand"
                          id="brand"
                          value={formData.brand}
                          onChange={handleChange}
                          required
                          placeholder={t('vehicles.form.brand')}
                        />
                        <label htmlFor="brand">{t('vehicles.form.brand')} *</label>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          name="model"
                          id="model"
                          value={formData.model}
                          onChange={handleChange}
                          required
                          placeholder={t('vehicles.form.model')}
                        />
                        <label htmlFor="model">{t('vehicles.form.model')} *</label>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-floating">
                        <input
                          type="number"
                          className="form-control"
                          name="year"
                          id="year"
                          value={formData.year}
                          onChange={handleChange}
                          min="1900"
                          max={new Date().getFullYear() + 1}
                          required
                          placeholder={t('vehicles.form.year')}
                        />
                        <label htmlFor="year">{t('vehicles.form.year')} *</label>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-floating">
                        <select
                          className="form-select"
                          name="type"
                          id="type"
                          value={formData.type}
                          onChange={handleChange}
                          required
                          placeholder={t('vehicles.form.type')}
                        >
                          <option value="">{t('vehicles.form.selectType')}</option>
                          <option value="HEAVY">{t('vehicles.types.heavy')}</option>
                          <option value="LIGHT">{t('vehicles.types.light')}</option>
                        </select>
                        <label htmlFor="type">{t('vehicles.form.type')} *</label>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-floating">
                        <input
                          type="number"
                          className="form-control"
                          name="capacity"
                          id="capacity"
                          value={formData.capacity}
                          onChange={handleChange}
                          min="0"
                          step="0.1"
                          required
                          placeholder={t('vehicles.form.capacity')}
                        />
                        <label htmlFor="capacity">{t('vehicles.form.capacity')} *</label>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          name="color"
                          id="color"
                          value={formData.color}
                          onChange={handleChange}
                          placeholder={t('vehicles.form.color')}
                        />
                        <label htmlFor="color">{t('vehicles.form.color')}</label>
                      </div>
                    </div>

                    {/* Insurance Information */}
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          name="insuranceNumber"
                          id="insuranceNumber"
                          value={formData.insuranceNumber}
                          onChange={handleChange}
                          placeholder={t('vehicles.form.insuranceNumber')}
                        />
                        <label htmlFor="insuranceNumber">{t('vehicles.form.insuranceNumber')}</label>
                      </div>
                    </div>

                    {/* Maintenance Information */}
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="date"
                          className="form-control"
                          name="lastMaintenanceDate"
                          id="lastMaintenanceDate"
                          value={formData.lastMaintenanceDate}
                          onChange={handleChange}
                          placeholder={t('vehicles.form.lastMaintenanceDate')}
                        />
                        <label htmlFor="lastMaintenanceDate">{t('vehicles.form.lastMaintenanceDate')}</label>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          type="date"
                          className="form-control"
                          name="nextMaintenanceDate"
                          id="nextMaintenanceDate"
                          value={formData.nextMaintenanceDate}
                          onChange={handleChange}
                          placeholder={t('vehicles.form.nextMaintenanceDate')}
                        />
                        <label htmlFor="nextMaintenanceDate">{t('vehicles.form.nextMaintenanceDate')}</label>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="col-12">
                      <label className="form-label">{t('vehicles.form.status')}</label>
                      <select
                        className="form-select"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                      >
                        <option value="AVAILABLE">{t('vehicles.status.available')}</option>
                        <option value="UNAVAILABLE">{t('vehicles.status.unavailable')}</option>
                        <option value="IN_MAINTENANCE">{t('vehicles.status.inMaintenance')}</option>
                        <option value="IN_USE">{t('vehicles.status.inUse')}</option>
                      </select>
                    </div>

                    {/* Vehicle Documents (Carte grise) */}
                    <div className="col-12 mt-4">
                      <hr className="text-muted opacity-25" />
                      <h3 className="h6 fw-bold mb-3 text-uppercase small text-muted">
                        {t('vehicles.documents.title') || 'Documents (Carte Grise)'}
                      </h3>

                      <div
                        className="dropzone p-4 bg-light border border-2 border-dashed rounded-3 text-center"
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        style={{ cursor: 'pointer' }}
                        onClick={() => document.getElementById('vehiculeFileInput').click()}
                      >
                        <input
                          id="vehiculeFileInput"
                          type="file"
                          multiple
                          className="d-none"
                          onChange={handleFileInputChange}
                        />
                        <i className="bi bi-cloud-arrow-up fs-1 text-primary mb-2 d-block"></i>
                        <button type="button" className="btn btn-sm btn-primary mb-2">
                          {t('vehicles.documents.browse') || 'Parcourir'}
                        </button>
                        <p className="text-muted small mb-0">{t('vehicles.documents.dragDrop') || 'Glissez-déposez vos fichiers ici'}</p>
                      </div>

                      {documents.length > 0 && (
                        <div className="mt-3">
                          <p className="small fw-bold text-muted mb-2">{t('vehicles.documents.queued') || 'Fichiers en attente'}:</p>
                          {documents.map((d, idx) => (
                            <div key={idx} className="card p-2 mb-2 bg-light border-0">
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-2">
                                  <i className="bi bi-file-earmark-pdf text-danger"></i>
                                  <span className="small text-truncate" style={{ maxWidth: '12.5rem' }}>{d.file.name}</span>
                                </div>
                                <button type="button" className="btn btn-sm text-danger" onClick={(e) => { e.stopPropagation(); removeDocument(idx); }}>
                                  <i className="bi bi-x-lg"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {isEditMode && existingDocuments.length > 0 && (
                        <div className="mt-4">
                          <p className="small fw-bold text-muted mb-2">{t('vehicles.documents.existing') || 'Documents existants'}:</p>
                          <div className="row g-2">
                            {existingDocuments.map(doc => (
                              <div key={doc.id} className="col-md-6">
                                <div className="card p-2 border shadow-none h-100">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center gap-2">
                                      <i className="bi bi-file-earmark-check text-success"></i>
                                      <span className="small text-truncate" style={{ maxWidth: '7.5rem' }}>{doc.name || 'Document'}</span>
                                    </div>
                                    <div className="d-flex gap-1">
                                      <a href={getFileUrl(doc.fileUrl)} target="_blank" rel="noreferrer" className="btn btn-sm btn-link p-0">
                                        <i className="bi bi-eye"></i>
                                      </a>
                                      <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={async (e) => {
                                        e.stopPropagation();
                                        if (window.confirm(t('common.confirmDelete') || 'Supprimer ce document ?')) {
                                          try {
                                            await api.delete(`/transporteur/documents/${doc.id}`);
                                            setExistingDocuments(prev => prev.filter(d => d.id !== doc.id));
                                          } catch (_) { }
                                        }
                                      }}>
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Submit Button */}
                    <div className="col-12 mt-4">
                      <div className="d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              {t('common.loading')}
                            </>
                          ) : (
                            <>
                              <i className="bi bi-save me-2"></i>
                              {isEditMode ? t('vehicles.updateButton') : t('vehicles.addButton')}
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => navigate('/vehicules')}
                        >
                          {t('common.close')}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default VehiculeForm; 